import type {RefObject} from 'react';
import type {ContainerQueryCondition} from '@Hooks/useContainerQuery';

export enum EventKeys {
  ENTER_KEY = 'Enter',
}

export enum MouseButton {
  PRIMARY = 0,
  MIDDLE = 1,
  SECONDARY = 2,
}

export enum FLEX_DIRECTION {
  COLUMN = 'column',
  ROW = 'row',
}

export const TABLET_SCREEN_BREAKPOINT = 768;
export const LARGE_MOBILE_SCREEN_BREAKPOINT = 550;
// Dropdown compares with `<`, the mobile layout applies at `<=`.
export const LARGE_MOBILE_DROPDOWN_MODAL_THRESHOLD = LARGE_MOBILE_SCREEN_BREAKPOINT + 1;
export const MOBILE_SCREEN_BREAKPOINT = 480;

const TEXT_ENTRY_INPUT_TYPES = ['text', 'number', 'search', 'email', 'url', 'password', 'tel'];

/**
 * Whether keyboard input currently goes to a text-entry element (a typeable input, a textarea,
 * or a contenteditable). Callers use this to avoid stealing focus from a field the user is
 * typing in; non-typing controls (buttons, checkboxes, sliders) intentionally return false.
 */
/** Suppresses text selection for the duration of a pointer drag (a resize grip, a timeline scrub); pair with `restoreBodyTextSelection`. */
export const disableBodyTextSelection = (): void => {
  document.body.style.userSelect = 'none';
};

export const restoreBodyTextSelection = (): void => {
  document.body.style.userSelect = '';
};

export const isTextEntryElementFocused = (): boolean => {
  return isTextEntryElement(document.activeElement);
};

/**
 * Whether the given element takes typed input. Prefer this over the focused-element check when handling
 * a key event: a field may blur itself on Enter before the event bubbles up, so `event.target` is the
 * reliable record of where the key went.
 */
export const isTextEntryElement = (element: EventTarget | null): boolean => {
  if (element instanceof HTMLInputElement) {
    return TEXT_ENTRY_INPUT_TYPES.includes(element.type);
  }
  if (element instanceof HTMLTextAreaElement) {
    return true;
  }
  return element instanceof HTMLElement && element.isContentEditable;
};

export const isContainerOverflowing = (container: HTMLElement): boolean => {
  return container.scrollHeight - container.clientHeight > 0;
};

export const scrollToTopOfElement = (element: HTMLElement, onScrolled: VoidFunction): void => {
  element.scrollTo({top: 0, behavior: 'smooth'});
  const checkIfScrolled = (): void => {
    if (element.scrollTop <= 0) {
      onScrolled();
    } else {
      requestAnimationFrame(checkIfScrolled);
    }
  };

  requestAnimationFrame(checkIfScrolled);
};

/**
 * `occludedBottomHeight` trims the container's viewport from the bottom for chrome that overlays it
 * (e.g. the editor's timeline bar), so covered pixels don't count as visible.
 */
export const getVisibleHeightOfElement = (element: HTMLElement, container: HTMLElement, occludedBottomHeight = 0): number => {
  const {scrollTop} = container;
  const scrollBottom = scrollTop + container.clientHeight - occludedBottomHeight;

  const elementRect = element.getBoundingClientRect();
  const elementTop = elementRect.top - container.getBoundingClientRect().top + scrollTop;
  const elementBottom = elementTop + elementRect.height;
  const visibleTop = elementTop < scrollTop ? scrollTop : elementTop;
  const visibleBottom = elementBottom > scrollBottom ? scrollBottom : elementBottom;

  return visibleBottom - visibleTop;
};

export function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  if (!el) {
    return null;
  }

  let parent: HTMLElement | null = el.parentElement;

  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
}

/**
 * Calculates the max visible height of each child element inside the scrollabeContainer and returns the one with the max height.
 * @param childElements
 * @param scrollabeContainer
 */
export const getChildElementWithMaxVisibleHeight = (childElements: HTMLElement[], scrollabeContainer: HTMLElement, occludedBottomHeight = 0): HTMLElement | undefined => {
  let visibleElementHeight = 0;
  let maxVisibleElementHeight = 0;
  let maxVisibleElementHeightIndex = 0;

  for (let i = 0; i < childElements.length; i++) {
    // Gets the amount of pixels currently visible within the scrollabeContainer
    visibleElementHeight = getVisibleHeightOfElement(childElements[i], scrollabeContainer, occludedBottomHeight);

    if (visibleElementHeight > maxVisibleElementHeight) {
      maxVisibleElementHeightIndex = i;
      maxVisibleElementHeight = visibleElementHeight;
    } else if (visibleElementHeight < 0) {
      break;
    }
  }
  return childElements[maxVisibleElementHeightIndex];
};

export const isTabletScreenWidth = (): boolean => {
  return window.innerWidth <= TABLET_SCREEN_BREAKPOINT;
};

export const isLargeMobileScreenWidth = (): boolean => {
  return window.innerWidth <= LARGE_MOBILE_SCREEN_BREAKPOINT;
};

export const isMobileScreenWidth = (): boolean => {
  return window.innerWidth <= MOBILE_SCREEN_BREAKPOINT;
};

export const addClassToElement = (elementId: string, classToAdd: string): void => {
  document.getElementById(elementId)?.classList?.add(classToAdd);
};

export const removeClassFromElement = (elementId: string, classToRemove: string): void => {
  document.getElementById(elementId)?.classList?.remove(classToRemove);
};

export const isTextElement = (element: Element): boolean => {
  const tagName = element.tagName.toLowerCase();
  if (tagName === 'textarea') return true;
  if (tagName !== 'input') return false;
  let type = element.getAttribute('type');
  if (type) {
    type = type.toLowerCase();
    // if any of these input types is not supported by a browser, it will behave as input type text.
    const inputTypes = ['text', 'password', 'number', 'email', 'tel', 'url', 'search', 'date', 'datetime', 'datetime-local', 'time', 'month', 'week'];
    return inputTypes.indexOf(type) >= 0;
  }
  return false;
};

export const areChecksTrueForContainerQueryBreakpoint = (containerQueryBreakpoint: ContainerQueryCondition, domElementWidth: number, domElementHeight: number): boolean => {
  if (containerQueryBreakpoint.maxHeight && !(domElementHeight <= containerQueryBreakpoint.maxHeight)) {
    return false;
  }
  if (containerQueryBreakpoint.maxWidth && !(domElementWidth <= containerQueryBreakpoint.maxWidth)) {
    return false;
  }
  if (containerQueryBreakpoint.minHeight && !(domElementHeight >= containerQueryBreakpoint.minHeight)) {
    return false;
  }

  if (containerQueryBreakpoint.minWidth && !(domElementWidth >= containerQueryBreakpoint.minWidth)) {
    return false;
  }

  return true;
};

/**
 * checks whether an element is visible completely in the viewport only horizontally
 * @param element
 */
export const isHorizontallyInViewport = (element: HTMLElement): boolean => {
  const boundingBox = element.getBoundingClientRect();
  return boundingBox.left >= 0 && boundingBox.right <= (document.documentElement.clientWidth || document.body.clientWidth || window.innerWidth);
};

export const isOverflowingViewportFromTop = (element: HTMLElement): boolean => {
  return element.getBoundingClientRect().top < 0;
};

export const addClickOutsideListener = (elementQuerySelector: string, callback: () => void | Promise<void>): VoidFunction => {
  const handleClickOutside = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const element = document.querySelectorAll(elementQuerySelector)[0];

    if (element && !element.contains(target)) {
      void callback();
    }
  };

  document.addEventListener('click', handleClickOutside, {capture: true});

  return (): void => {
    document.removeEventListener('click', handleClickOutside, {capture: true});
  };
};

export const scrollToBottom = (container: HTMLDivElement | null, behavior: ScrollBehavior = 'smooth'): void => {
	if (!container) {
		return;
	}

	container.scrollTo({top: container.scrollHeight, behavior: behavior});
};

export const scrollToBottomNextFrame = (containerRef: RefObject<HTMLDivElement | null>, behavior: ScrollBehavior = 'smooth'): void => {
	requestAnimationFrame(() => {
		scrollToBottom(containerRef.current, behavior);
	});
};

/**
 * Crops the left half of a canvas to a DataURL, optionally compositing an overlay canvas
 * on top and applying a vertical scale to the main canvas content (e.g. for HiDPI display).
 *
 * @param canvas - The main canvas to crop.
 * @param overlayCanvas - An optional overlay drawn on top of the left portion.
 * @param overlayWidthPx - How many canvas pixels of the overlay to draw (from the left).
 * @param yScale - Vertical scale applied to the main canvas content when drawing (>1 stretches).
 */
export const cropCanvasLeftHalf = (
  canvas: HTMLCanvasElement,
  overlayCanvas: HTMLCanvasElement | null,
  overlayWidthPx: number,
  yScale: number
): string | null => {
  const halfWidth = Math.floor(canvas.width / 2);
  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = halfWidth;
  cropCanvas.height = canvas.height;
  const ctx = cropCanvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  if (overlayWidthPx > 0 && overlayCanvas) {
    ctx.drawImage(overlayCanvas, 0, 0, overlayWidthPx, overlayCanvas.height, 0, 0, overlayWidthPx, cropCanvas.height);
  }
  if (yScale > 1) {
    ctx.save();
    ctx.translate(0, canvas.height / 2);
    ctx.scale(1, yScale);
    ctx.translate(0, -canvas.height / 2);
    ctx.drawImage(canvas, 0, 0, halfWidth, canvas.height, 0, 0, halfWidth, canvas.height);
    ctx.restore();
  } else {
    ctx.drawImage(canvas, 0, 0, halfWidth, canvas.height, 0, 0, halfWidth, canvas.height);
  }
  return cropCanvas.toDataURL();
};

export const autoResizeTextarea = (
  el: HTMLTextAreaElement,
  minHeight = 38,
  maxHeight = 188
): void => {
  el.rows = 1;
  el.style.minHeight = '0';
  el.style.maxHeight = `${maxHeight}px`;
  el.style.height = 'auto';
  const contentHeight = el.scrollHeight;
  const lineHeight = parseInt(getComputedStyle(el).lineHeight, 10) || 20;
  const minRows = Math.ceil(minHeight / lineHeight);
  el.rows = Math.max(minRows, Math.min(Math.ceil(contentHeight / lineHeight), Math.ceil(maxHeight / lineHeight)));
  const height = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${height}px`;
  el.style.minHeight = `${minHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
};
/** True when a wheel event's magnitude looks like a discrete mouse-wheel notch rather than trackpad inertia. */
export function isMouseWheelEvent(e: WheelEvent): boolean {
  const absDelta = Math.abs(e.deltaY);
  return absDelta >= 10;
}

/** Reads a JSON payload the server serialised onto an element's data attribute. Null if unreadable. */
export function readJsonDataAttribute<T>(element: HTMLElement, attributeName: string): Partial<T> | null {
  try {
    // A missing attribute parses as '' and throws, so it lands on the same branch as bad JSON.
    return JSON.parse(element.getAttribute(attributeName) ?? '') as Partial<T>;
  } catch {
    return null;
  }
}
